import { postprocessTokenization, TPreprocessorResult, TTokenizeResult, wrappedPreprocessor, wrappedTokenizer } from '../../elf-core/ElfTokenizer';
import { buildAST, TSyntaxTree, TSyntaxTreeBuilderContext } from '../../elf-core/ElfAst';
import { AstCompileWarning, CompileError } from '../../elf-core/ElfCompileErrors';
import * as astCtx from '../../elf-core/ElfDefaultAstContext';

export type TElfCompilerNotice = { message: string; line: number; column: number; endLine: number; endColumn: number };
export type TElfUserError = TElfCompilerNotice;
export type TElfUserWarning = TElfCompilerNotice;
export type TElfUserCompilationResultSuccess = { elfCode: string; compiled: true; tokens: TTokenizeResult; ast: { tree: TSyntaxTree; treeCtx: TSyntaxTreeBuilderContext }; warnings: TElfUserWarning[]; errors: [] };
export type TElfUserCompilationResult = (
    TElfUserCompilationResultSuccess |
    { compiled: false; tokens: TTokenizeResult; ast: null; warnings: TElfUserWarning[]; errors: TElfUserError[] }
);

export const mapCompileError = (preprocessRes: TPreprocessorResult, compileError: CompileError | AstCompileWarning): TElfCompilerNotice => {
	let line = preprocessRes.characters.at(-1)?.line ?? 1;
	let column = preprocessRes.characters.at(-1)?.column ?? 1;
	let endLine = line;
	let endColumn = column;

	if (!('eof' in compileError.startPos)) {
		line = compileError.startPos.line;
		column = compileError.startPos.column;
	}
	if (!('eof' in compileError.endPos)) {
		endLine = compileError.endPos.line;
		endColumn = compileError.endPos.column;
	}

	// we need to increase end column as endPos always points to the COLUMN of the last character.
	// as we are displaying errors, we want to move that column by + 1 so we wrap the whole char/word/statement!
	endColumn++;

	return { message: compileError.message, line, column, endLine, endColumn };
};

export function compileElfCode(code: string, compileConfig: { registers: number }): TElfUserCompilationResult {
	// eslint-disable-next-line no-console
	console.time('preprocessor');
	const [preprocessRes, preprocessExc] = wrappedPreprocessor(code);

	// eslint-disable-next-line no-console
	console.timeEnd('preprocessor');
	// eslint-disable-next-line no-console
	console.time('tokenizer');
	const [tokenizerRes, tokenizerExc] = wrappedTokenizer(preprocessRes.characters);

	// eslint-disable-next-line no-console
	console.timeEnd('tokenizer');
	// eslint-disable-next-line no-console
	console.time('post-tokenizer');
	postprocessTokenization(tokenizerRes);
	// eslint-disable-next-line no-console
	console.timeEnd('post-tokenizer');

	const compileError: CompileError | undefined = (preprocessExc?.compileError ?? tokenizerExc?.compileError);
	const errors: TElfUserError[] = [];
	const warnings: TElfUserWarning[] = [];

	if (compileError) {
		errors.push(mapCompileError(preprocessRes, compileError));
	}

	let astRuntime: TSyntaxTree | null = null;
	let astRuntimeBuildCtx: TSyntaxTreeBuilderContext | null = null;

	if (!preprocessExc && !tokenizerExc) {
		const astBuildCtx = astCtx.build({ maxNumberOfRegisters: compileConfig.registers });

		// eslint-disable-next-line no-console
		console.time('build-ast');
		const astResult = buildAST(tokenizerRes.astReadyTokens, astBuildCtx);

		// eslint-disable-next-line no-console
		console.timeEnd('build-ast');

		warnings.push(...astResult.warnings.map((warning) => mapCompileError(preprocessRes, warning)));
		errors.push(...astResult.errors.map((error) => mapCompileError(preprocessRes, error)));

		if (astResult.ast) {
			astRuntime = astResult.ast;
			astRuntimeBuildCtx = astBuildCtx;
		}
	}

	if (astRuntime !== null && astRuntimeBuildCtx !== null) {
		return { compiled: true, ast: { tree: astRuntime, treeCtx: astRuntimeBuildCtx }, tokens: tokenizerRes, warnings, errors: [], elfCode: code };
	}

	return { compiled: false, ast: null, tokens: tokenizerRes, warnings, errors };
}
